// src/pages/Home.js
// Page d'accueil Fynho - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Logo from '../components/shared/Logo';
import CategoryBar from '../components/home/CategoryBar';
import SearchBar from '../components/home/SearchBar';
import GeoButton from '../components/home/GeoButton';
import ListingRow from '../components/listing/ListingRow';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import ThemeToggle from '../components/shared/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import { generateOrganizationSchema } from '../utils/seo';
import ROUTES from '../constants/routes';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [allListings, setAllListings] = useState([]);
  const [sponsoredListings, setSponsoredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadListings = async () => {
      setLoading(true);
      setError(false);
      try {
        const [allResult, sponsoredResult] = await Promise.all([
          apiClient.get('/endpoints/listings.php?action=list&status=published&limit=40'),
          apiClient.get('/endpoints/listings.php?action=list&status=published&sponsored=1&limit=10'),
        ]);
        setAllListings(allResult.listings || []);
        setSponsoredListings(sponsoredResult.listings || []);
      } catch (err) {
        setError(true);
        setAllListings([]);
        setSponsoredListings([]);
      } finally {
        setLoading(false);
      }
    };
    loadListings();
  }, []);

  const sejours = allListings.filter((l) => l.category === 'sejours');
  const locations = allListings.filter((l) => l.category === 'locations');
  const immobilier = allListings.filter((l) => l.category === 'immobilier');
  const services = allListings.filter((l) => l.category === 'services');

  const organizationSchema = generateOrganizationSchema();

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}` },
    headerControls: { display: 'flex', alignItems: 'center', gap: '8px' },
    rowsContainer: { paddingTop: '16px' },
    stateMessage: { textAlign: 'center', padding: '48px 24px', color: theme.textSecondary, fontSize: '14px' },
    emptyContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', textAlign: 'center', gap: '12px' },
    emptyIcon: { fontSize: '48px', opacity: 0.4 },
    emptyTitle: { fontSize: '16px', fontWeight: '600', color: theme.textPrimary },
    emptyText: { fontSize: '14px', color: theme.textSecondary, maxWidth: '280px', lineHeight: '1.5' },
    footer: { padding: '32px 16px 24px', textAlign: 'center', borderTop: `1px solid ${theme.borderSoft}` },
    footerText: { fontSize: '12px', color: theme.textMuted, fontWeight: '500' },
    footerYear: { fontSize: '11px', color: theme.textMuted, marginTop: '8px' },
    mapButton: { position: 'fixed', bottom: '84px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: theme.textPrimary, color: theme.bg, border: 'none', borderRadius: '24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', zIndex: 95, boxShadow: theme.shadow },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Fynho - Find Your New Home | Immobilier au Cameroun</title>
        <meta name="description" content="Trouvez votre logement idéal au Cameroun : séjours, locations, terrains et services à Douala, Yaoundé et plus." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Fynho - Find Your New Home" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fynho.com/" />
        <link rel="canonical" href="https://fynho.com/" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div style={styles.header}>
        <Logo size="medium" showText={true} />
        <div style={styles.headerControls}>
          <ThemeToggle compact />
          <LanguageSwitcher compact />
        </div>
      </div>

      <SearchBar />
      <GeoButton />
      <CategoryBar />

      {loading && <div style={styles.stateMessage}>{t('common.loading')}</div>}
      {!loading && error && <div style={styles.stateMessage}>{t('errors.networkError')}</div>}

      {!loading && !error && allListings.length === 0 && (
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>🏡</div>
          <div style={styles.emptyTitle}>Aucune annonce pour le moment</div>
          <div style={styles.emptyText}>Les premières annonces publiées apparaîtront ici très bientôt.</div>
        </div>
      )}

      {!loading && !error && allListings.length > 0 && (
        <div style={styles.rowsContainer}>
          {sponsoredListings.length > 0 && (
            <ListingRow icon="⭐" title={t('sections.premiumTitle')} description={t('sections.premiumDesc')} listings={sponsoredListings} />
          )}
          <ListingRow icon="🏨" title={t('sections.sejoursTitle')} description={t('sections.sejoursDesc')} listings={sejours} seeAllRoute={ROUTES.SEJOURS} />
          <ListingRow icon="🏠" title={t('sections.locationsTitle')} description={t('sections.locationsDesc')} listings={locations} seeAllRoute={ROUTES.LOCATIONS} />
          <ListingRow icon="🌍" title={t('sections.immobilierTitle')} description={t('sections.immobilierDesc')} listings={immobilier} seeAllRoute={ROUTES.IMMOBILIER} />
          <ListingRow icon="🛎️" title={t('sections.servicesTitle')} description={t('sections.servicesDesc')} listings={services} seeAllRoute={ROUTES.SERVICES} />
        </div>
      )}

      <div style={styles.footer}>
        <div style={styles.footerText}>Fynho</div>
        <div style={styles.footerYear}>© {new Date().getFullYear()}</div>
      </div>

      <button style={styles.mapButton} onClick={() => navigate(ROUTES.MAP_SEARCH)}>
        🗺️ {t('home.viewMap')}
      </button>
    </div>
  );
};

export default Home;